<template>

  <div class="data-table">

    <div id="search">

      <el-input
        v-if="showSearch"
        :placeholder="$t('placeholder.search')"
        icon="el-icon-search"
        v-model="searchText"
        />

    </div>

    <el-popover
      placement="left"
      class="column-selector top"
      v-model="isColumSelectorVisible">

      <el-checkbox-group v-model="selectedColumns">
        <el-checkbox class="column-selector-checkbox" v-for="col in normalizedColumns" :label="$t('header.' + col)" :value="col"></el-checkbox>
      </el-checkbox-group>

      <el-button slot="reference" @click="isColumSelectorVisible = true" type="primary" circle icon="el-icon-more"/>
    </el-popover>

    <el-pagination
      v-if="pagination.includes('top')"
      class="pagination top"
      layout="sizes, prev, pager, next"
      :total="total"
      :page-size="localSize"
      :current-page="currentPage"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      />

    <el-table
      ref="table"
      :data="translatedData"
      :row-key="rowKey"
      border
      stripe
      :highlight-current-row="singleSelect"
      :empty-text="$t('empty-table')"
      v-loading.body="loading"
      @selection-change="handleSelectionChange"
      @sort-change="handleSortChange"
      @row-click="rowClick">

      <el-table-column
        v-if="isSelected"
        type="selection"
        width="49"
        :reserve-selection="true"/>

      <el-table-column
        v-for="col in orderedSelectedColumns"
        :prop="col"
        :label="$t('header.' + col)"
        sortable
        :sort-method="sortMethod"
      />

      <el-table-column
        v-for="cell in plusCells">

        <template scope="scope">

          <component :is="cell" :row="scope.row" />

        </template>

      </el-table-column>

      <el-table-column
        v-if="showOperationsColumn()"
        :label="$t('header.operations')">

        <template scope="scope">

          <el-button-group>

            <el-button
              v-for="op in operations"
              v-if="isOperationVisible(op, scope.row)"
              :key="op.label"
              size="mini"
              :disabled="op.disabled && op.disabled(scope.row)"
              :type="op.type"
              :icon="op.icon && 'el-icon-' + op.icon"
              @click="op.method(scope.row)">{{ op.label ? $t('operation.' + op.label) : '' }}</el-button>

          </el-button-group>

        </template>

      </el-table-column>

    </el-table>

    <el-pagination
      v-if="pagination.includes('bottom')"
      class="pagination bottom"
      layout="sizes, prev, pager, next"
      :total="total"
      :page-size="localSize"
      :current-page="currentPage"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      />

  </div>

</template>

<script>
import _ from 'lodash'

export default {
  name: 'data-table',
  props: {
    model: {
      type: [String, Array],
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    size: {
      type: Number,
      default: 10
    },
    /*
      Example operation:

      {
        label: 'label',
        permission: 'CREATE_STUFF',
        type: 'danger',
        icon: 'search',
        method: (row) => {},
        precondition: (row) => {},
      }
    */
    operations: {
      type: Array,
      default: () => []
    },
    plusCells: {
      type: Array
    },
    isSelected: {
      type: Function,
      required: false
    },
    pagination: {
      type: Array,
      default: () => ['top']
    },
    singleSelect: {
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    order: {
      type: Object,
      default: () => ({
        sortBy: null,
        ascending: true
      })
    },
    disableLoadingIndicator: {
      type: Boolean,
      default: false
    },
    customSort: {
      type: Function
    }
  },
  data () {
    return {
      isColumSelectorVisible: false,
      selectedColumns: this.computeNormalizedColumns(),
      currentPage: 1,
      localSize: this.size,
      sortBy: this.order.sortBy,
      ascending: this.order.ascending,
      tableDataChanged: false,
      searchText: null
    }
  },
  computed: {
    tableData () {
      if (typeof this.model === 'string') {
        return this.$store.getters[`${this.model}/data`](this.$store.dispatch)
      }
      else if (this.model instanceof Array) {
        return this.model
      }
    },
    filteredData () {
      // Creating a copy of the tableData by slice
      let paginatedData = this.tableData.slice()

      if (!this.searchText) {
        return paginatedData
      }

      return paginatedData.filter((item) => {
        let match = false

        Object.forEach(item, ({ key, value }) => {
          // Filter only by visible columns
          if (!this.columns.includes(key)) {
            return
          }

          if (value &&
              value.includes instanceof Function && // If it has includes method
              value.toLowerCase().includes(this.searchText.toLowerCase())) {
            match = true
          }
        })

        return match
      })
    },
    paginatedData () {
      // ===============
      // Filter
      // ===============

      let paginatedData = this.filteredData

      // ===============
      // Sort
      // ===============

      paginatedData = paginatedData.sort((a, b) => this._sort(a, b) * (this.ascending ? 1 : -1))

      // ===============
      // Paginate
      // ===============

      const from = (this.currentPage - 1) * this.localSize
      const to = from + this.localSize

      return paginatedData.slice(from, to)
    },
    translatedData () {
      const translateColumns = this.columns.filter(c => c.includes('[T]')).map(c => c.replace('[T]', ''))

      return this.paginatedData.map(row => {
        return {
          ...row,
          ...translateColumns.reduce((acc, curr) => ({ ...acc, [curr]: this.$t(row[curr]) }), {})
        }
      })
    },
    normalizedColumns () {
      return this.computeNormalizedColumns()
    },
    orderedSelectedColumns () {
      this.selectedColumns.sort((a, b) => this.normalizedColumns.indexOf(a) - this.normalizedColumns.indexOf(b))

      return this.selectedColumns
    },
    total () {
      return this.filteredData.length
    },
    loading () {
      if (this.disableLoadingIndicator) {
        return false
      }

      return this.$store.getters[`${this.model}/loading`]
    }
  },
  mounted () {
    this.tableDataChanged = true
  },
  beforeUpdate () {
    if (!this.tableDataChanged) return
    if (!this.isSelected || !this.$refs.table) return

    this.tableDataChanged = false

    this.tableData.forEach(row => {
      this.$refs.table.toggleRowSelection(row, this.isSelected(row))
    })
  },
  methods: {
    isOperationVisible (operation, row) {
      if (!operation.precondition && !operation.permission) {
        return true
      }

      if (operation.permission) {
        if (_.isArray(operation.permission) && !this.hasPermission(operation.permission[0], row[operation.permission[1]])) {
          return false
        }
        else if (_.isString(operation.permission) && !this.hasPermission(operation.permission)) {
          return false
        }
      }

      if (operation.precondition) {
        if (_.isFunction(operation.precondition) && !operation.precondition(row)) {
          return false
        }
      }

      return true
    },
    handleSortChange ({ prop, order }) {
      this.sortBy = prop
      this.ascending = order !== 'descending'
    },
    handleSizeChange (size) {
      this.localSize = size
    },
    handleCurrentChange (currentPage) {
      this.currentPage = currentPage
    },
    handleSelectionChange (selection) {
      this.$emit('selection-change', selection)
    },
    sortMethod (a, b) {
      // The return value should be boolean and ignore ordering (it will be done at the callers side)!
      return this._sort(a, b) === 1
    },
    _sort (a, b) {
      if (this.customSort) {
        return this.customSort(a, b)
      }

      const sortBy = this.sortBy || this.columns[0]

      if (a[sortBy] instanceof String) {
        return a[sortBy].localeCompare(b[sortBy])
      }

      return a[sortBy] > b[sortBy] ? 1 : -1
    },
    rowClick (row, event, column) {
      this.$emit('row-click', row, event, column)
    },
    showOperationsColumn () {
      // Test every row and operation pair if any operation should be visible
      return this.operations.filter((op) => {
        return this.paginatedData.filter((row) => this.isOperationVisible(op, row)).length
      }).length
    },
    computeNormalizedColumns () {
      return this.columns.map(c => c.replace('[T]', ''))
    }
  },
  watch: {
    tableData () {
      this.tableDataChanged = true
    },
    '$lang' () {
      this.tableDataChanged = true
    },
    size (v) {
      this.localSize = v
    },
    columns () {
      this.selectedColumns = this.computeNormalizedColumns()
    }
  }
}
</script>

<style scoped lang="scss">
.data-table {
  margin-top: 20px;
  margin-bottom: 20px;
}

.pagination {
  float: right;

  &.top {
    margin-bottom: 10px;
  }

  &.bottom {
    margin-top: 10px;
  }
}

.column-selector {
  float: right;
  vertical-align: center;
}

.column-selector-checkbox {
  display: block;
  margin-left: 0;
}

#search > .el-input {
  float: left;
  width: 250px;
}
</style>



// WEBPACK FOOTER //
// DataTable.vue?5dc04c72